import { Environment, Lightformer, useGLTF, useTexture } from '@react-three/drei';
import { Canvas } from '@react-three/fiber';
import { Physics } from '@react-three/rapier';
import confetti from 'canvas-confetti';
import { AnimatePresence, motion } from 'framer-motion';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { useEffect, useMemo, useRef, useState } from 'react';
import * as THREE from 'three';
import { useWindowSize } from '@/lib/hooks/useWindowSize';
import logger from '@/utils/logger';
import { Band } from '@/pages/HomeDesktop/Community/Card';

// Enable ColorManagement globally
THREE.ColorManagement.enabled = true;

useGLTF.preload(
  'https://assets.vercel.com/image/upload/contentful/image/e5382hct74si/5huRVDzcoDwnbgrKUo1Lzs/53b6dd7d6b4ffcdbd338fa60265949e1/tag.glb',
);
useTexture.preload(
  'https://assets.vercel.com/image/upload/contentful/image/e5382hct74si/SOT1hmCesOHxEYxL7vkoZ/c57b29c85912047c414311723320c16b/band.jpg',
);

const decodeParams = (encoded) => {
  try {
    const base64Decoded = atob(encoded);
    const uriDecoded = decodeURIComponent(base64Decoded);
    const params = JSON.parse(uriDecoded);
    return params;
  } catch (error) {
    logger.error(error, { context: 'decodeParams' });
    return null;
  }
};

export default function ShareCard() {
  const router = useRouter();
  const { code } = router.query;
  const canvasRef = useRef(null);
  const { width, height } = useWindowSize();
  const [showText, setShowText] = useState(true);
  const [showCard, setShowCard] = useState(false);

  // Try to get parameters from encoded format
  const params = useMemo(() => {
    if (!code) return null;

    const decodedParams = decodeParams(code);

    if (decodedParams?.firstName && decodedParams.lastName && decodedParams.type) {
      return decodedParams;
    }

    // Use defaults if no valid parameters
    return {
      firstName: 'John',
      lastName: 'Doe',
      type: 'powerUsers',
    };
  }, [code]);

  // Only destructure if params exists
  const firstName = params?.firstName || 'John';
  const lastName = params?.lastName || 'Doe';
  const type = params?.type || 'powerUsers';

  useEffect(() => {
    // Prevent scrolling and bouncing effects
    const preventDefault = (e) => e.preventDefault();
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.touchAction = 'none';
    document.documentElement.style.overflow = 'hidden';
    document.documentElement.style.touchAction = 'none';
    document.addEventListener('touchmove', preventDefault, { passive: false });
    document.addEventListener('scroll', preventDefault, { passive: false });

    return () => {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.touchAction = '';
      document.documentElement.style.overflow = '';
      document.documentElement.style.touchAction = '';
      document.removeEventListener('touchmove', preventDefault);
      document.removeEventListener('scroll', preventDefault);
    };
  }, []);

  useEffect(() => {
    const textTimer = setTimeout(() => {
      setShowText(false);
    }, 2500);

    const cardTimer = setTimeout(() => {
      setShowCard(true);

      // Delay confetti animation
      setTimeout(() => {
        const duration = 5 * 1000;
        const animationEnd = Date.now() + duration;
        const defaults = {
          startVelocity: 30,
          spread: 360,
          ticks: 60,
          zIndex: 9999,
        };

        const randomInRange = (min, max) => Math.random() * (max - min) + min;

        const interval = setInterval(() => {
          const timeLeft = animationEnd - Date.now();

          if (timeLeft <= 0) {
            return clearInterval(interval);
          }

          const particleCount = 50 * (timeLeft / duration);
          confetti({
            ...defaults,
            particleCount,
            origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 },
          });
          confetti({
            ...defaults,
            particleCount,
            origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 },
          });
        }, 250);
      }, 1800); // Delay fireworks by 1.8 seconds after card appears
    }, 3000);

    return () => {
      clearTimeout(textTimer);
      clearTimeout(cardTimer);
    };
  }, []);

  return (
    <div
      className="fixed inset-0 w-screen h-screen bg-black touch-none overscroll-none"
      onTouchMove={(e) => e.preventDefault()}
      onScroll={(e) => e.preventDefault()}
    >
      <Head>
        <title>
          {firstName} {lastName} is a Verified ClickUp{' '}
          {type === 'champions' ? 'Champion' : type === 'consultants' ? 'Verified Consultant' : 'Power User'}!
        </title>
        <meta
          property="og:title"
          content={`${firstName} ${lastName} is a Verified ClickUp ${
            type === 'champions' ? 'Champion' : type === 'consultants' ? 'Verified Consultant' : 'Power User'
          }!`}
        />
        <meta property="og:description" content="Check out my ClickUp verification badge!" />
        <meta property="og:image" content="/ClickupWhiteLogo.png" />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary" />
      </Head>
      <AnimatePresence>
        {showText && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{
              duration: 0.8,
              ease: 'easeOut',
            }}
            className="fixed inset-0 flex items-center justify-center bg-black z-10"
          >
            <motion.h1
              className="text-4xl md:text-6xl font-medium text-center"
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              transition={{
                duration: 0.8,
                ease: 'easeOut',
              }}
            >
              <span className="text-white">
                {firstName} {lastName} is a
              </span>
              <br />
              <span
                className={`font-bold mt-4 block ${
                  type === 'champions' ? 'text-[#5982FD]' : type === 'consultants' ? 'text-[#D73659]' : 'text-[#AB22F3]'
                } text-3xl md:text-6xl`}
              >
                Verified ClickUp{' '}
                {type === 'champions' ? 'Champion' : type === 'consultants' ? 'Verified Consultant' : 'Power User'}!
              </span>
            </motion.h1>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showCard && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{
              duration: 1,
              ease: 'easeOut',
            }}
            className="fixed inset-0 w-full h-full"
          >
            <div className="fixed inset-0 w-full h-full">
              <Canvas ref={canvasRef} camera={{ position: [0, 0, 13], fov: 25 }} className="w-full h-full">
                <color attach="background" args={['#000000']} />
                <ambientLight intensity={Math.PI} />
                <Physics interpolate gravity={[0, -40, 0]} timeStep={1 / 60}>
                  <Band firstName={firstName} lastName={lastName} type={type} />
                </Physics>
                <Environment background blur={0.75}>
                  <color attach="background" args={['black']} />
                  <Lightformer
                    intensity={2}
                    color="white"
                    position={[0, -1, 5]}
                    rotation={[0, 0, Math.PI / 3]}
                    scale={[100, 0.1, 1]}
                  />
                  <Lightformer
                    intensity={3}
                    color="white"
                    position={[-1, -1, 1]}
                    rotation={[0, 0, Math.PI / 3]}
                    scale={[100, 0.1, 1]}
                  />
                  <Lightformer
                    intensity={3}
                    color="white"
                    position={[1, 1, 1]}
                    rotation={[0, 0, Math.PI / 3]}
                    scale={[100, 0.1, 1]}
                  />
                  <Lightformer
                    intensity={10}
                    color="white"
                    position={[-10, 0, 14]}
                    rotation={[0, Math.PI / 2, Math.PI / 3]}
                    scale={[100, 10, 1]}
                  />
                </Environment>
              </Canvas>
            </div>
            <div className="fixed top-8 left-8 z-10">
              <img src="/ClickupWhiteLogo.png" alt="ClickUp Logo" className="w-32 h-auto" />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
